/**
 * Shared terminal-action helpers for a restaurant (Directions / Call / Share / Reserve).
 *
 * These are the SINGLE implementation used by both the restaurant detail page (RestaurantInfo) and
 * the restaurant card (RestaurantCard), so the "get me there / call them / share it / book it"
 * behaviour never drifts between surfaces. All of them work for anonymous users — no auth gate here.
 */

/** Google Maps fallback used when we have nothing to point at (no coords, place id, or address). */
const GOOGLE_MAPS_FALLBACK =
  'https://www.google.com/maps/dir/?api=1&destination='

export interface DirectionsInput {
  latitude?: number | null
  longitude?: number | null
  placeId?: string | null
  /** A raw (unencoded) human address string, e.g. "123 Main St, Austin, TX". */
  address?: string | null
}

/** True when the raw address string carries at least one letter/number worth mapping. */
const addressHasContent = (address?: string | null): boolean =>
  !!address && /[a-z0-9]/i.test(address)

const hasCoords = (
  latitude?: number | null,
  longitude?: number | null,
): boolean =>
  typeof latitude === 'number' &&
  Number.isFinite(latitude) &&
  typeof longitude === 'number' &&
  Number.isFinite(longitude)

/**
 * Build a Google Maps URL for a restaurant. Prefers lat/lng, then the Google `placeId`, then a raw
 * address string; falls back to a bare directions URL when nothing is available. This is the one
 * implementation the detail page and the card both call.
 */
export function buildDirectionsUrl({
  latitude,
  longitude,
  placeId,
  address,
}: DirectionsInput): string {
  if (hasCoords(latitude, longitude)) {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
  }
  if (placeId) {
    return `https://www.google.com/maps/search/?api=1&query=%20&query_place_id=${placeId}`
  }
  if (addressHasContent(address)) {
    return `https://www.google.com/maps/place/${encodeURIComponent(
      (address as string).trim(),
    )}`
  }
  return GOOGLE_MAPS_FALLBACK
}

/** Whether we can produce a meaningful directions link (used to hide the Directions action). */
export function hasDirections({
  latitude,
  longitude,
  placeId,
  address,
}: DirectionsInput): boolean {
  return (
    hasCoords(latitude, longitude) || !!placeId || addressHasContent(address)
  )
}

/** A `tel:` href for the phone number, or undefined when there's no number to call. */
export function buildCallHref(phone?: string | null): string | undefined {
  const trimmed = phone?.trim()
  return trimmed ? `tel:${trimmed}` : undefined
}

export interface ReservationInput {
  /** A dedicated booking/reservation destination (OpenTable/Resy/etc.), when the restaurant has one. */
  reservationUrl?: string | null
  /** The restaurant's website — the fallback booking hand-off when there's no reservation URL. */
  website?: string | null
}

/**
 * The URL a "Reserve" hand-off should open — the dedicated reservation page when present, else the
 * restaurant's website (mirrors the detail page's `reservationUrl ?? website` behaviour). Undefined
 * when neither exists, so the caller hides the action. This is the one implementation the detail page
 * and the card both lean on for the reservation referral.
 */
export function buildReservationUrl({
  reservationUrl,
  website,
}: ReservationInput): string | undefined {
  const reservation = reservationUrl?.trim()
  if (reservation) {
    return reservation
  }
  const site = website?.trim()
  if (site) {
    return site
  }
  return undefined
}

/** Whether we can hand the user off to book (used to hide the Reserve action). */
export function hasReservation(input: ReservationInput): boolean {
  return !!buildReservationUrl(input)
}
